import { FC, useMemo, useRef } from 'react'

import { getCustomComponent } from '../../utils/VL/context/sharedContext'
import { componentCatalog } from '../components/lazy/componentCatalog'

const componentCache = new Map<string, FC>()
const capitalCache = new Map<string, string>()

const toCapital = (str: string): string => {
  if (capitalCache.has(str)) {
    return capitalCache.get(str) ?? ''
  }

  const words = str.split('_')
  let result = ''
  for (const word of words) {
    result += word.charAt(0).toUpperCase() + word.slice(1)
  }

  capitalCache.set(str, result)
  return result
}

/**
 * Resolves a component by name, checking the cache first.
 * Library components: component catalog (static + dynamic).
 * Custom components: shared context (value stream modules).
 */
const resolveComponent = (
  name: string,
  isPureReact: boolean,
  cache: Map<string, FC>
): FC | undefined => {
  if (!name) return undefined

  const cacheKey = `${isPureReact ? 'custom' : 'library'}:${name}`
  const cached = cache.get(cacheKey)
  if (cached) return cached

  let component: FC

  if (isPureReact) {
    component = getCustomComponent(name) as FC
  } else {
    const capitalName = toCapital(name)
    // eslint-disable-next-line security/detect-object-injection
    component = componentCatalog[capitalName] as FC
  }

  if (component) cache.set(cacheKey, component)
  return component
}

/**
 * Resolves a React component by block type name.
 *
 * Library components are memoized (component catalog never changes).
 * Custom components retry on each render because the shared context
 * is populated asynchronously via configureContextualStore (useEffect).
 */
export const useComponent = (name: string, isPureReact = false): FC => {
  const cacheRef = useRef(componentCache)

  const memoized = useMemo(
    () => resolveComponent(name, isPureReact, cacheRef.current),
    [name, isPureReact]
  )

  if (!memoized && isPureReact && name) {
    return resolveComponent(name, isPureReact, cacheRef.current)
  }

  return memoized
}

export default useComponent
